import { useCallback, useEffect, useRef, useState } from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  googleAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder';
import { useTemplateSecrets } from '@backstage/plugin-scaffolder-react';
import {
  Alert,
  Box,
  Button,
  Flex,
  Link,
  Radio,
  RadioGroup,
  Skeleton,
  Text,
} from '@backstage/ui';

/**
 * Secret the caller's Google ID token travels in.
 *
 * Must match CONTROL_ID_TOKEN_SECRET in
 * packages/backend/src/modules/scaffolderControl. A scaffolder task runs long
 * after the browser has gone, so the token cannot be lifted from a request —
 * the secrets channel is how a task acts as the human who started it, and it
 * keeps the token out of the task's parameters and logs.
 */
const CONTROL_ID_TOKEN_SECRET = 'CONTROL_ID_TOKEN';

/** Where a request goes when the portal cannot self-serve it. */
const DEVOPS_DESK = 'https://devops.answerrocket.com';

/**
 * What `placement.recommend` resolves — launchpad's `Recommendation`, verbatim.
 *
 * Two fields are worth staring at, because confusing them is what broke every
 * service request the portal ever made: **`env` and `project` are BOTH the
 * environment's id** (`answerrocket-internal`), and **`tier` is the tier**
 * (`internal`). Control's `serviceSpecSchema.env` wants the TIER. So the value
 * this field's consumer needs for Control's `env` is `tier`, and the one it
 * needs for `project` is `project`.
 */
export interface Placement {
  action?: 'use_existing' | 'request_new_env' | 'devops_required';
  intent?: string;
  cloud?: string;
  /** The environment id. Same value as `project`. */
  env?: string;
  project?: string;
  company?: string;
  owner_group?: string;
  tier?: string;
  audience?: string;
  domain?: string;
  branch?: string;
  stability?: string;
  rationale?: string;
  alternatives?: {
    env: string;
    cloud?: string;
    tier?: string;
    rationale?: string;
  }[];
  suggested_environment?: {
    cloud?: string;
    tier?: string;
    company?: string;
    owner_group?: string;
    audience?: string;
  };
  /** Set when the resolver needs another fact before it can decide. */
  need_context?: {
    field: string;
    question: string;
    options?: { id: string; label: string }[];
  };
}

/**
 * "Where should this land?" — answered by policy, never by the form.
 *
 * # How this works, and why it is a conversation rather than a dropdown
 *
 * launchpad's resolver is deterministic but not omniscient: given an intent it
 * may still need to know which company owns the work, or whether the workload
 * needs its own GCP project. Rather than guessing, it answers with a
 * `need_context` block naming the field, the question to ask, and the options.
 *
 * The first version of this field rendered that block as a sentence and
 * stopped. That made the two intents carrying real customer work — `product`
 * and `client-engagement`, the only two that set `requires_company` and
 * `asks_isolation` — impossible to use at all, and the form still submitted an
 * unresolved placement to Control. It also shipped a hardcoded intent list
 * whose values (`customer-solution`, `experiment`) launchpad had never heard
 * of, so those resolved to a `need_context` for `intent` itself.
 *
 * So: ask the resolver with nothing, render whatever question comes back,
 * feed the answer in, repeat until it decides. The intent list is the
 * resolver's first question, not a constant in this file — which is why a new
 * intent added to `placement.yaml` appears here with no portal change.
 *
 * The field is only SATISFIED when the answer is `use_existing`. The other two
 * outcomes are real answers, not errors, and each gets a fork:
 *
 *   - `request_new_env` — policy says this needs its own project. That is the
 *     other golden path, and the field says so instead of letting someone
 *     submit a service request with no project in it.
 *   - `devops_required` — a non-GCP cloud, which is not self-serve yet. Link
 *     to the request desk rather than render a form that cannot work.
 *
 * It also collects the Google ID token, because this is the field every
 * request-a-thing template already has to include.
 */
export const PlacementPicker = (
  props: FieldExtensionComponentProps<Placement>,
) => {
  const {
    onChange,
    schema: { description },
    formData,
    formContext,
  } = props;

  // `required`/`rawErrors` are not read. RJSF's error furniture drew an empty
  // slot on every render, and this field enforces the harder rule itself: it
  // only satisfies the form when policy actually chose somewhere, so an
  // unresolved placement blocks Next without needing a validation message.
  const discovery = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const googleAuth = useApi(googleAuthApiRef);
  const { setSecrets } = useTemplateSecrets();

  // Every fact the resolver has asked for and been given, in the shape its
  // Input takes. Keyed by the `need_context.field` it came back with, so a new
  // question needs no code here.
  const [answers, setAnswers] = useState<Record<string, string>>(
    formData?.intent ? { intent: formData.intent } : {},
  );
  const [placement, setPlacement] = useState<Placement | undefined>(formData);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  /**
   * Whether we hold the caller's Google identity.
   *
   * `unknown` before the first attempt, `missing` when the silent path failed.
   * Missing is recoverable and worth saying out loud here rather than letting
   * the task die on its first step with an error written for whoever wrote the
   * template.
   */
  const [identity, setIdentity] = useState<'unknown' | 'ok' | 'missing'>(
    'unknown',
  );

  /** Retry from a click, which is a user gesture, so a popup is permitted. */
  const connectIdentity = async () => {
    try {
      const idToken = await googleAuth.getIdToken();
      if (idToken) {
        setSecrets({ [CONTROL_ID_TOKEN_SECRET]: idToken });
        setIdentity('ok');
      }
    } catch {
      setIdentity('missing');
    }
  };

  // The service name, when the template has already collected it, so the
  // resolver can suggest the domain this service will actually get. Read
  // defensively: a template that has no `name` field must still work.
  const name =
    typeof (formContext as { formData?: Record<string, unknown> })?.formData
      ?.name === 'string'
      ? ((formContext as { formData: Record<string, string> }).formData
          .name as string)
      : undefined;

  // onChange is not referentially stable across renders in RJSF, so holding it
  // in a ref keeps `resolve` stable and the mount effect running exactly once.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const resolve = useCallback(
    async (context: Record<string, string>) => {
      setBusy(true);
      setError(undefined);
      try {
        // Collect the caller's Google identity into the scaffolder's secrets
        // channel. This is what `control:rbac:preflight` and Control itself
        // evaluate at submit time — the request is made AS THEM.
        //
        // Best-effort, and deliberately NOT fatal here. `getIdToken()` can
        // come back empty when the browser blocks the silent refresh, and
        // failing placement over it stopped the whole wizard on a call that
        // does not need the token at all. What it does instead is record that
        // identity is missing, so the step that DOES need it can say so with a
        // button the user can press — a popup opened from a click is allowed
        // where one opened from an effect is not.
        try {
          const idToken = await googleAuth.getIdToken();
          if (idToken) {
            setSecrets({ [CONTROL_ID_TOKEN_SECRET]: idToken });
            setIdentity('ok');
          } else {
            setIdentity('missing');
          }
        } catch {
          setIdentity('missing');
        }

        const baseUrl = await discovery.getBaseUrl('control');
        const response = await fetchApi.fetch(`${baseUrl}/placement`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...context, ...(name ? { name } : {}) }),
        });
        if (!response.ok) {
          throw new Error(`${response.status} ${await response.text()}`);
        }
        const result = (await response.json()) as Placement;
        setPlacement(result);
        // Satisfy the field ONLY when policy actually chose somewhere. An
        // unresolved placement used to be emitted anyway, so the form
        // submitted and the task died four steps later with a repository
        // already created.
        onChangeRef.current(
          result.action === 'use_existing' && result.project
            ? result
            : (undefined as unknown as Placement),
        );
      } catch (e) {
        setError(
          // The reason only. The Alert supplies "Could not resolve placement"
          // as its title, and carrying it here too printed the sentence twice.
          e instanceof Error ? e.message : String(e),
        );
        setPlacement(undefined);
        onChangeRef.current(undefined as unknown as Placement);
      } finally {
        setBusy(false);
      }
    },
    [discovery, fetchApi, googleAuth, name, setSecrets],
  );

  useEffect(() => {
    // Ask with whatever we already have — usually nothing, in which case the
    // resolver's first answer IS the intent question.
    void resolve(answers);
    // Once on mount; every later resolution is driven by an answer below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const answer = (field: string, value: string) => {
    const next = { ...answers, [field]: value };
    setAnswers(next);
    void resolve(next);
  };

  /** Drop the last answer so a wrong turn is one click to undo, not a reload. */
  const startOver = () => {
    setAnswers({});
    void resolve({});
  };

  const need = placement?.need_context;
  const decided = placement?.action === 'use_existing' && placement.project;

  const rows: [string, string | undefined][] = [
    ['Environment', placement?.project],
    ['Tier', placement?.tier],
    ['Domain', placement?.domain],
    ['Owner', placement?.owner_group],
    ['Deploys from', placement?.branch],
  ];

  const Fork = (props2: { title: string; children: React.ReactNode }) => (
    <Box
      style={{
        border: '1px solid var(--bui-border-1)',
        borderLeft: '3px solid var(--ar-accent)',
        borderRadius: 'var(--bui-radius-2)',
        padding: '12px 14px',
        marginTop: 10,
      }}
    >
      <Text as="p" weight="bold" style={{ marginBottom: 6 }}>
        {props2.title}
      </Text>
      {props2.children}
      <Box mt="3">
        <Button size="small" variant="secondary" onPress={startOver}>
          Change my answers
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box>
      <Text
        as="p"
        variant="body-small"
        color="secondary"
        style={{ marginBottom: 12 }}
      >
        {description ??
          'Policy decides where this lands. Answer what you are building; launchpad resolves the rest.'}
      </Text>

      {error && (
        <Alert
          status="warning"
          title="Could not resolve placement"
          description={error}
        />
      )}

      {identity === 'missing' && (
        <Box mb="3">
          <Alert
            status="warning"
            title="We could not read your Google identity"
            description="Control acts as the person asking, so the request needs it. Your browser may have blocked the silent sign-in refresh."
            customActions={
              <Button size="small" onPress={() => void connectIdentity()}>
                Connect Google identity
              </Button>
            }
          />
        </Box>
      )}

      {busy && <Skeleton style={{ height: 88 }} />}

      {/* The resolver needs another fact. Render its question, its options, and
          nothing of our own — the vocabulary belongs to placement.yaml. */}
      {!busy && need && (
        <RadioGroup
          label={need.question}
          value={answers[need.field] ?? ''}
          onChange={value => answer(need.field, value)}
        >
          {(need.options ?? []).map(option => (
            <Radio key={option.id} value={option.id}>
              {option.label}
            </Radio>
          ))}
        </RadioGroup>
      )}

      {/* Policy chose. Read-only facts — there is deliberately no free-text
          project box: a hand-typed project is how a service ends up in the
          wrong account. Values are mono because they are identifiers, and
          `answerrocket-internal` vs `answerrocket-intemal` is one glyph. */}
      {!busy && decided && (
        <Box>
          <Box
            style={{
              border: '1px solid var(--bui-border-1)',
              borderRadius: 'var(--bui-radius-2)',
              overflow: 'hidden',
            }}
          >
            {rows.map(([label, value], i) => (
              <Flex
                key={label}
                justify="between"
                align="center"
                style={{
                  padding: '8px 12px',
                  borderTop: i === 0 ? 'none' : '1px solid var(--bui-border-2)',
                }}
              >
                <Text variant="body-small" color="secondary">
                  {label}
                </Text>
                <Text
                  variant="body-small"
                  style={{ fontFamily: 'var(--ar-font-mono)' }}
                >
                  {value ?? '—'}
                </Text>
              </Flex>
            ))}
          </Box>
          {placement?.rationale && (
            <Text
              as="p"
              variant="body-small"
              color="secondary"
              style={{ marginTop: 10 }}
            >
              {placement.rationale}
            </Text>
          )}
          {!!placement?.alternatives?.length && (
            <Text
              as="p"
              variant="body-small"
              color="secondary"
              style={{ marginTop: 6 }}
            >
              Also matched: {placement.alternatives.map(a => a.env).join(', ')}{' '}
              — policy ranks the one above first.
            </Text>
          )}
          <Box mt="3">
            <Button size="small" variant="secondary" onPress={startOver}>
              Change my answers
            </Button>
          </Box>
        </Box>
      )}

      {/* Policy says this needs its own GCP project. Not an error — the other
          golden path, named as such. */}
      {!busy && placement?.action === 'request_new_env' && (
        <Fork title="This needs its own GCP project">
          <Text as="p" variant="body-small">
            {placement.rationale ??
              'No existing environment matches what you are building.'}
          </Text>
          <Text
            as="p"
            variant="body-small"
            color="secondary"
            style={{ marginTop: 6 }}
          >
            A new environment is a new billing boundary and a new IAM boundary,
            so it is always reviewed by DevOps.{' '}
            <Link href="/create/templates/new-env">Request one</Link>, then come
            back here once it exists — it starts routing automatically.
          </Text>
        </Fork>
      )}

      {/* Non-GCP. Honest degradation beats a form that cannot work. */}
      {!busy && placement?.action === 'devops_required' && (
        <Fork
          title={`${(
            placement.cloud ?? 'That cloud'
          ).toUpperCase()} is not self-serve yet`}
        >
          <Text as="p" variant="body-small">
            {placement.rationale ??
              'The portal can only provision GCP today. AWS and Azure go through DevOps.'}
          </Text>
          <Text
            as="p"
            variant="body-small"
            color="secondary"
            style={{ marginTop: 6 }}
          >
            <Link href={DEVOPS_DESK}>File a request</Link> and it will be picked
            up from the queue.
          </Text>
        </Fork>
      )}
    </Box>
  );
};
